import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  ConvertLeadsRequest,
  ConvertLeadsResponse,
  ReviveLeadsRequest,
  CheckLeadRequest,
  CreateLeadRequest,
  CreateLeadResponse,
  GetLeadsByCuratorRequest,
  AssignCuratorToLeadRequest,
  ChangeLeadStatusRequest,
  UpdateLeadNotesRequest,
  RejectLeadsRequest,
  GetLeadsByManagerRequest,
  GetLeadsByWorkerRequest,
  GetLeadsByHeadRequest,
  GetManagerLeadsDataRequest,
  RateLeadRequest,
  AdvanceLeadIrrelevanceRequest,
  RestoreLeadIrrelevanceRequest,
} from "./api.dto";
import { PaginatedResponse } from "@/shared/types";
import { LeadByHead, LeadByManager, LeadByWorker } from "../model";
import { useToastNotifications } from "@/shared/hooks";
import { Roles } from "@/entities/user";

export const useChangeLeadStatus = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: { description: "Статус лидa обновлён" },
    },
    refetchQueries: ["leads"],
  });

  return useMutation({
    mutationKey: ["changeLeadStatus"],
    mutationFn: async ({ leadId, status }: ChangeLeadStatusRequest) => {
      const res = await apiInstance.put(
        `/leads/${leadId}/status`,
        { status },
        true,
      );
      return res.data;
    },
    onSuccess,
    onError,
  });
};

export const useUpdateLeadNote = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: { description: "Комментарий обновлён" },
    },
    refetchQueries: ["leads"],
  });

  return useMutation({
    mutationKey: ["updateLeadNote"],
    mutationFn: async ({ leadId, note }: UpdateLeadNotesRequest) => {
      const res = await apiInstance.put(
        `/leads/${leadId}/note`,
        { note },
        true,
      );
      return res.data;
    },
    onSuccess,
    onError,
  });
};

export const useRejectLead = () => {
  return useMutation({
    mutationKey: ["rejectLead"],
    mutationFn: async ({ leadId, reason }: RejectLeadsRequest) => {
      const res = await apiInstance.post(
        `/leads/${leadId}/reject`,
        { reason },
        undefined,
        true,
      );
      return res.data;
    },
    onSuccess: () => {
      const queryClient = useQueryClient();
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};

export const useSendLeadInfo = () => {
  return useMutation({
    mutationKey: ["sendLeadInfo"],
    mutationFn: async (leadId: string) => {
      const response = await apiInstance.post<{ message: "accepted" }>(
        `/leads/${leadId}/dispatch`,
        undefined,
        undefined,
        true,
      );

      return response.data.message;
    },
  });
};

export const useSendLeadToCurator = () => {
  return useMutation({
    mutationKey: ["sendLeadDataToCurator"],
    mutationFn: async (leadId: string) => {
      const response = await apiInstance.post<{ message: "accepted" }>(
        `/leads/${leadId}/send-to-curator`,
        undefined,
        undefined,
        true,
      );

      return response.data.message;
    },
  });
};

export const useAdvanceLeadIrrelevance = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Данные обновлены.",
      },
    },
    refetchQueries: ["leads"],
  });

  return useMutation({
    mutationKey: ["advanceLeadIrrelevance"],
    mutationFn: async (data: AdvanceLeadIrrelevanceRequest) => {
      const response = await apiInstance.post<{ message: "accepted" }>(
        `/leads/${data.leadId}/irrelevance/advance`,
        undefined,
        undefined,
        true,
      );
      return response.data.message;
    },
    onSuccess,
    onError,
  });
};

export const useRestoreLeadIrrelevance = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Данные обновлены.",
      },
    },
    refetchQueries: ["leads"],
  });

  return useMutation({
    mutationKey: ["restoreLeadIrrelevance"],
    mutationFn: async (data: RestoreLeadIrrelevanceRequest) => {
      const response = await apiInstance.post<{ message: "accepted" }>(
        `/leads/${data.leadId}/irrelevance/restore`,
        {
          status: data.status,
          note: data.note,
          rating: data.rating,
        },
        undefined,
        true,
      );
      return response.data.message;
    },
    onSuccess,
    onError,
  });
};

export const useConvertLeads = () => {
  return useMutation({
    mutationKey: ["converLeads"],
    mutationFn: async (data: ConvertLeadsRequest) => {
      const response = await apiInstance.post<ConvertLeadsResponse>(
        "/leads/convert",
        data,
        undefined,
        true,
      );

      return response.data;
    },
  });
};

export const useReviveLeads = () => {
  return useMutation({
    mutationKey: ["reviveLeads"],
    mutationFn: async (data: ReviveLeadsRequest) => {
      const response = await apiInstance.post<{ message: string }>(
        "/leads/revive",
        data,
        undefined,
        true,
      );

      return response.data;
    },
  });
};

export const useCheckLead = (data: CheckLeadRequest) => {
  return useQuery({
    queryKey: ["lead", data],
    queryFn: async () => {
      const response = await apiInstance.head("/leads", data, true);

      return response.status === 200;
    },
    enabled: !!data.telegramId,
  });
};

export const useCreateLead = () => {
  return useMutation({
    mutationKey: ["createLead"],
    mutationFn: async (data: CreateLeadRequest) =>
      await apiInstance.post<CreateLeadResponse>(
        "/leads",
        data,
        undefined,
        true,
      ),
  });
};

export const useReassignCuratorToLead = ({
  fromCuratorId,
  toCuratorId,
}: {
  fromCuratorId: string;
  toCuratorId: string;
}) => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Куратор перепривязан.",
      },
    },
  });
  return useMutation<
    AxiosResponse,
    AxiosError,
    AssignCuratorToLeadRequest,
    Response
  >({
    mutationKey: ["assignCuratorToLead"],
    mutationFn: () => {
      return apiInstance.patch(
        `/leads?curatorIds=${fromCuratorId}&sync=true`,
        {
          curatorId: {
            isSet: true,
            value: toCuratorId,
          },
        },
        {},
        true,
      );
    },
    onSuccess,
    onError,
  });
};

export const useLeadsByWorker = (data: GetLeadsByWorkerRequest) => {
  return useQuery<PaginatedResponse<LeadByWorker>>({
    queryKey: ["leads", "worker", filterObjectKeys(data, ["enabled"])],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByWorker>>(
        `/leads/worker/${data.userId}`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useLeadsByCurator = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: ["leads", "curator", filterObjectKeys(data, ["enabled"])],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/curator/${data.userId}`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useIrrelevantLeadsByCurator = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: [
      "leads",
      "curator",
      "irrelevant",
      filterObjectKeys(data, ["enabled"]),
    ],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/curator/${data.userId}/irrelevant`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useLeadsByCuratorInf = (
  _data: Omit<GetLeadsByCuratorRequest, "currentPage"> & { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: ["leads", "curator", filterObjectKeys(_data)],
    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/curator/${_data.curatorId}`,
        {
          ...filterObjectKeys(_data, ["curatorId"]),
          currentPage: pageParam,
        },
        true,
      );

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = 5;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: _data.enabled,
  });
};

export const useLeadsByManager = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: ["leads", "manager", filterObjectKeys(data, ["enabled"])],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/manager/${data.userId}`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useIrrelevantLeadsByManager = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: [
      "leads",
      "manager",
      "irrelevant",
      filterObjectKeys(data, ["enabled"]),
    ],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/manager/${data.userId}/irrelevant`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useLeadsByTeamlead = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: ["leads", "teamlead", filterObjectKeys(data, ["enabled"])],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/teamlead/${data.userId}`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useIrrelevantLeadsByTeamlead = (
  data: GetLeadsByManagerRequest,
) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: [
      "leads",
      "teamlead",
      "irrelevant",
      filterObjectKeys(data, ["enabled"]),
    ],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/teamlead/${data.userId}/irrelevant`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useLeadsByMasterTeamlead = (data: GetLeadsByManagerRequest) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: ["leads", "masterTeamlead", filterObjectKeys(data, ["enabled"])],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/master-teamlead/${data.userId}`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useIrrelevantLeadsByMasterTeamlead = (
  data: GetLeadsByManagerRequest,
) => {
  return useQuery<PaginatedResponse<LeadByManager>>({
    queryKey: [
      "leads",
      "masterTeamlead",
      "irrelevant",
      filterObjectKeys(data, ["enabled"]),
    ],
    enabled: data.enabled && !!data.userId,
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByManager>>(
        `/leads/master-teamlead/${data.userId}/irrelevant`,
        filterObjectKeys(data, ["userId", "enabled"]),
        true,
      );

      return response.data;
    },
  });
};

export const useManagerLeadsData = (data: GetManagerLeadsDataRequest) => {
  const filteredData = filterObjectKeys(data, ["byCurator", "role", "enabled"]);

  const curatorQuery = useLeadsByCurator({
    ...filteredData,
    userId: data.userId,
    enabled: data.byCurator,
  });

  const curatorIrrelevantQuery = useIrrelevantLeadsByCurator({
    ...filteredData,
    userId: data.userId,
    enabled: data.byCurator,
  });

  const managerQuery = useLeadsByManager({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.Manager && !data.byCurator,
  });

  const teamleadQuery = useLeadsByTeamlead({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.TeamLead && !data.byCurator,
  });

  const masterTeamleadQuery = useLeadsByMasterTeamlead({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.MasterTeamLead && !data.byCurator,
  });

  const managerIrrelevantQuery = useIrrelevantLeadsByManager({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.Manager && !data.byCurator,
  });

  const teamleadIrrelevantQuery = useIrrelevantLeadsByTeamlead({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.TeamLead && !data.byCurator,
  });

  const masterTeamleadIrrelevantQuery = useIrrelevantLeadsByMasterTeamlead({
    ...filteredData,
    userId: data.userId,
    enabled: data.role === Roles.MasterTeamLead && !data.byCurator,
  });

  if (data.byCurator)
    return {
      leads: curatorQuery,
      irrelevantLeads: curatorIrrelevantQuery,
    };

  switch (data.role) {
    case Roles.Manager:
      return {
        leads: managerQuery,
        irrelevantLeads: managerIrrelevantQuery,
      };
    case Roles.TeamLead:
      return {
        leads: teamleadQuery,
        irrelevantLeads: teamleadIrrelevantQuery,
      };
    case Roles.MasterTeamLead:
      return {
        leads: masterTeamleadQuery,
        irrelevantLeads: masterTeamleadIrrelevantQuery,
      };
    default:
      return {
        leads: managerQuery,
        irrelevantLeads: managerIrrelevantQuery,
      };
  }
};

export const useLeadsByHead = (data: GetLeadsByHeadRequest) => {
  return useQuery<PaginatedResponse<LeadByHead>>({
    queryKey: ["leads", filterObjectKeys(data)],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<LeadByHead>>(
        "/leads",
        filterObjectKeys(data),
        true,
      );

      return response.data;
    },
  });
};

export const useRateLead = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: { description: "Лид оценён" },
    },
    refetchQueries: ["leads"],
  });

  return useMutation({
    mutationKey: ["rateLead"],
    mutationFn: async (data: RateLeadRequest) => {
      const response = await apiInstance.put(
        `/leads/${data.leadId}/rating`,
        {
          rating: data.rating,
        },
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

// export const useExportCuratorLeads = ({
//   curatorId,
// }: ExportCuratorLeadsRequest) => {
//   return useQuery<Blob>({
//     queryKey: ["leads"],
//     queryFn: async () => {
//       const response = await apiInstance.get<ArrayBuffer>(
//         `/leads/export/xlsx/curator/${curatorId}`,
//         undefined,
//         false,
//         {
//           responseType: "arraybuffer",
//         },
//       );

//       const blob = new Blob([response.data], {
//         type: response.headers["content-type"],
//       });

//       // Создаем ссылку на Blob
//       const url = window.URL.createObjectURL(blob);

//       // Создаем элемент <a> для скачивания
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute("download", `curator_leads_${curatorId}.xlsx`); // Указываем имя файла
//       document.body.appendChild(link);

//       // Инициируем скачивание
//       link.click();

//       // Удаляем ссылку и освобождаем ресурсы
//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);

//       return blob;
//     },
//     enabled: false,
//     refetchOnWindowFocus: false,
//   });
// };

// export const useExportAllLeads = (params: {
//   fromDate?: Date;
//   toDate?: Date;
//   status?: string | null;
//   isStatusIrrelevant?: boolean | null;
// }) => {
//   return useMutation({
//     mutationKey: ["exportAllLeads", filterObjectKeys(params)],
//     mutationFn: async () => {
//       const fileResponse = await apiInstance.get<Blob>(
//         "/leads/export/xlsx",
//         filterObjectKeys(params),
//         false,
//         { responseType: "blob" },
//       );

//       try {
//         const arrayBuffer = await fileResponse.data.arrayBuffer();
//         const wb = read(arrayBuffer);

//         if (!wb) {
//           throw new Error("Не удалось прочитать файл");
//         }

//         const sheetName = wb.SheetNames[0];
//         const ws = wb.Sheets[sheetName];

//         const range = utils.decode_range(ws["!ref"]!);
//         for (let R = range.s.r + 1; R <= range.e.r; R++) {
//           const surveyCellAddress = utils.encode_cell({
//             r: R,
//             c: 3,
//           });
//           if (ws[surveyCellAddress] && ws[surveyCellAddress].v) {
//             const originalText = ws[surveyCellAddress].v;
//             const localizedText = localizationMap[originalText] || originalText;
//             ws[surveyCellAddress].v = localizedText;
//             ws[surveyCellAddress].t = "s";
//           }

//           const dateCellAddress = utils.encode_cell({ r: R, c: 8 });
//           if (ws[dateCellAddress] && ws[dateCellAddress].v) {
//             const originalDate = ws[dateCellAddress].v;

//             try {
//               const parsedDate = new Date(originalDate);
//               const formattedDate = format(parsedDate, "dd MMMM yyyy HH:mm", {
//                 locale: ru,
//               });
//               ws[dateCellAddress].v = formattedDate;
//               ws[dateCellAddress].t = "s";
//             } catch (error) {
//               console.error(`Ошибка форматирования даты в строке ${R}:`, error);
//             }
//           }

//           if (R % 2 === 1) {
//             for (let C = range.s.c; C <= range.e.c; C++) {
//               const cellAddress = utils.encode_cell({ r: R, c: C });
//               if (ws[cellAddress]) {
//                 ws[cellAddress].s = {
//                   fill: { patternType: "solid", fgColor: { rgb: "D9EAF9" } },
//                 };
//               }
//             }
//           }
//         }

//         const newWb = utils.book_new();
//         utils.book_append_sheet(newWb, ws, "All Leads");

//         try {
//           writeFile(
//             newWb,
//             `all_leads_${new Date().toISOString().slice(0, 10)}.xlsx`,
//           );
//         } catch (e) {
//           console.error("Ошибка при сохранении файла 2:", e);
//         }
//       } catch (error) {
//         console.error("Ошибка при сохранении файла:", error);
//       }
//     },
//   });
// };
